import React from 'react';
import { Facebook, Instagram, Phone, MapPin, Mail } from 'lucide-react';
import { LogoOption1 } from './LogoOptions';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Company Info */}
          <div>
            <div className="mb-4 [&_span]:!text-white">
              <LogoOption1 className="h-16" />
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-4">
              Licensed Mortgage Specialist and CPA helping Colorado homeowners 62+ 
              achieve financial freedom through reverse mortgages.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/profile.php?id=61577799387652" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/milehighreversemortgage" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-pink-600 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {[
                { label: 'Benefits', id: 'benefits' },
                { label: 'History', id: 'history' },
                { label: 'How It Works', id: 'process' },
                { label: 'Calculator', id: 'calculator' },
                { label: 'FAQ', id: 'faq' },
                { label: 'About', id: 'about' }
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-sm hover:text-blue-400 transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <a href="tel:+17203078711" className="text-sm hover:text-blue-400 transition-colors">
                    (720) 307-8711
                  </a>
                  <p className="text-xs text-gray-500">Mon-Fri: 8am - 6pm MT</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <a href="mailto:info@milehighreverse.com" className="text-sm hover:text-blue-400 transition-colors">
                  info@milehighreverse.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm">Serving all of Colorado</p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Compliance Disclaimer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h4 className="text-white font-semibold mb-3 text-sm">Important Disclosure</h4>
            <p className="text-xs text-gray-400 leading-relaxed">
              This website is for educational purposes only and is not a commitment to lend. 
              All reverse mortgage programs, including Home Equity Conversion Mortgages (HECMs), 
              are subject to qualification, credit approval, property eligibility, and program 
              guidelines established by HUD and individual lenders. Interest rates, fees, and 
              program terms may change without notice.
            </p>
            <p className="text-xs text-gray-400 leading-relaxed mt-3">
              The information provided is general in nature and should not be interpreted as 
              financial, tax, or legal advice. Clients should consult with their own financial 
              advisor, tax professional, or attorney regarding their specific situation. As a 
              Mortgage Loan Officer sponsored by C2 Financial Corporation, I provide guidance on mortgage 
              products only and do not provide tax or legal services. I am acting as your MLO, 
              not your CPA.
            </p>
            <p className="text-xs text-gray-400 leading-relaxed mt-3">
              Examples, scenarios, and projections are for illustrative purposes and may not 
              reflect actual loan terms or future performance. Borrowers remain responsible for 
              property taxes, homeowners' insurance, HOA dues (if applicable), and maintaining 
              the home. Failure to meet these obligations may result in foreclosure.
            </p>
            <p className="text-xs text-gray-400 leading-relaxed mt-3">
              All opinions expressed are my own and are intended to help you understand how 
              reverse mortgages work so you can make an informed decision. This presentation 
              does not replace required HUD counseling.
            </p>
          </div>

          {/* License Info */}
          <div className="text-center text-xs text-gray-500 space-y-1">
            <p>Peter Andrew S. | Licensed Mortgage Specialist | NMLS #2575736</p>
            <p>Sponsored by C2 Financial Corporation | Company NMLS #135622</p>
            <p className="pt-2">© {currentYear} Peter Andrew S. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Equal Housing */}
      <div className="border-t border-gray-800 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs text-gray-500">
            Equal Housing Lender | FHA Approved Lender
          </p>
        </div>
      </div>
    </footer>
  );
};
